/**
 * `cai verify --changed` — annotate which failures touch files you actually edited.
 *
 * Honest scope (per the backlog's own caveat): this does NOT re-scope the gate.
 * Typecheck/build stay GLOBAL, because a change to one file routinely breaks a
 * compile in another via shared symbols — scoping the gate would reimport the very
 * 80%-problem cai exists to fight, labelling real breakage as "not yours".
 *
 * What it does is purely ADDITIVE: it parses file paths out of each failing step's
 * output, intersects them with the changed-file set, and labels the step
 * "touches your changes" / "in unchanged files" so the agent can triage. The gate
 * is unchanged; only the message gains a hint.
 *
 * Parsing is per-ecosystem and best-effort — unrecognized output simply yields no
 * annotation rather than a wrong one. Deterministic, no AI, no network.
 */
import type { VerifyStep } from "./verify.js";

/**
 * Extract candidate source-file paths from a compiler/linter output blob.
 * Recognizes the common shapes:
 *   tsc:           src/foo.ts(12,3): error TS2322: ...
 *   tsc pretty:    src/foo.ts:12:3 - error TS2322: ...
 *   eslint/vitest: /abs/or/rel/path.ts:12:3
 *   generic:       path/to/file.ext:line[:col]
 */
export function extractFilePaths(output: string): string[] {
  const found = new Set<string>();
  // file.ext(line,col)  — TypeScript classic
  const tscParen = /([\w./\-]+\.[A-Za-z]{1,5})\((\d+),(\d+)\)/g;
  // file.ext:line:col   — most tools
  const colon = /([\w./\-]+\.[A-Za-z]{1,5}):(\d+)(?::(\d+))?/g;

  for (const re of [tscParen, colon]) {
    for (const m of output.matchAll(re)) {
      const raw = m[1];
      if (!raw || raw.startsWith("http")) continue;
      found.add(normalizePath(raw));
    }
  }
  return [...found];
}

function normalizePath(p: string): string {
  return p.replace(/^\.?\//, "");
}

export interface ChangedAnnotation {
  /** Paths in the failure output that intersect the changed set. */
  touchingChanges: string[];
  /** Whether any reported file is in the changed set. */
  touchesChanges: boolean;
  /** Whether we could parse any file paths at all (false ⇒ no reliable annotation). */
  parsed: boolean;
}

/**
 * Annotate a failed step against the changed-file set. Returns parsed=false when no
 * file paths could be extracted — callers should then omit the annotation rather than
 * imply the failure is unrelated to the agent's edits.
 */
export function annotateStep(step: VerifyStep, changedFiles: Set<string>): ChangedAnnotation {
  if (step.status !== "failed" || !step.output) {
    return { touchingChanges: [], touchesChanges: false, parsed: false };
  }
  const reported = extractFilePaths(step.output);
  if (reported.length === 0) {
    return { touchingChanges: [], touchesChanges: false, parsed: false };
  }
  const changedNorm = new Set([...changedFiles].map(normalizePath));
  const touching = reported.filter((p) => changedNorm.has(p));
  return { touchingChanges: touching, touchesChanges: touching.length > 0, parsed: true };
}

/** One-line annotation suffix for a failing step, or "" when nothing reliable to say. */
export function annotationLabel(annotation: ChangedAnnotation): string {
  if (!annotation.parsed) return "";
  if (annotation.touchesChanges) {
    return ` ⟶ touches your changes: ${annotation.touchingChanges.join(", ")}`;
  }
  return ` ⟶ all reported files are outside your uncommitted changes (likely pre-existing or shared-symbol fallout)`;
}
